import ProductType from "../../../enum/ProductType";

const HEX = "0123456789abcdef";

const getNonEmptyString = (value: unknown): string | null => {
  if (value === null || value === undefined) {
    return null;
  }

  const str = String(value).trim();
  return str.length > 0 ? str : null;
};

const generateHexWithMathRandom = (length: number): string => {
  let result = "";
  for (let i = 0; i < length; i += 1) {
    result += HEX[Math.floor(Math.random() * HEX.length)];
  }
  return result;
};

const generateHexWithCrypto = (length: number): string => {
  const cryptoObj = globalThis.crypto;
  if (!cryptoObj?.getRandomValues) {
    return generateHexWithMathRandom(length);
  }

  const byteCount = Math.ceil(length / 2);
  const bytes = new Uint8Array(byteCount);
  cryptoObj.getRandomValues(bytes);

  const randomHex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  return randomHex.slice(0, length);
};

export const generateObjectIdLikeLineId = (): string => {
  const timestampHex = Math.floor(Date.now() / 1000)
    .toString(16)
    .padStart(8, "0")
    .slice(-8);
  const randomHex = generateHexWithCrypto(16);

  return `${timestampHex}${randomHex}`;
};

export const resolveLineId = (line: any): string => {
  return (
    getNonEmptyString(line?.lineId) ??
    getNonEmptyString(line?.orderProductLineID) ??
    getNonEmptyString(line?._id) ??
    generateObjectIdLikeLineId()
  );
};

export const resolvePersistedSourceLineId = (line: unknown): string => {
  if (!line || typeof line !== 'object') return '';
  const persistedLine = line as {
    lineId?: unknown;
    orderProductLineID?: unknown;
    _id?: unknown;
  };

  return (
    getNonEmptyString(persistedLine.lineId) ??
    getNonEmptyString(persistedLine.orderProductLineID) ??
    getNonEmptyString(persistedLine._id) ??
    ''
  );
};

const resolveProductId = (line: any): string => {
  const storeProductId = line?.storeProductId;
  return (
    getNonEmptyString(line?.productId) ??
    (typeof storeProductId === "string" ? getNonEmptyString(storeProductId) : null) ??
    getNonEmptyString(storeProductId?._id) ??
    getNonEmptyString(line?._id) ??
    ""
  );
};

const resolveSelectedOptionId = (line: any): string => {
  return (
    getNonEmptyString(line?.selectedOptionId) ??
    getNonEmptyString(line?.selectedOption?._id) ??
    ""
  );
};

const resolveLineIdFromLineFields = (line: any): string | null => {
  return getNonEmptyString(line?.lineId) ?? getNonEmptyString(line?.orderProductLineID);
};

export const ensureLineIds = <T extends Record<string, any>>(lines: T[] = []): T[] => {
  return (Array.isArray(lines) ? lines : []).map((line) => {
    const lineId = resolveLineId(line);
    return {
      ...line,
      lineId,
      orderProductLineID: getNonEmptyString(line?.orderProductLineID) ?? lineId
    };
  });
};

export const buildBuyGetIdentityKey = (line: any): string => {
  const productId = resolveProductId(line);
  const selectedOptionId = resolveSelectedOptionId(line);
  const isBuyAndGetProduct = Boolean(line?.isBuyAndGetProduct);
  const isFreeItem = Boolean(line?.isFreeItem);

  return [productId, selectedOptionId, isBuyAndGetProduct ? "1" : "0", isFreeItem ? "1" : "0"].join(
    "|"
  );
};

export const stabilizeBuyAndGetLineIds = (previousBuyGet: any[] = [], nextBuyGet: any[] = []) => {
  const queuesByKey = new Map<string, string[]>();

  (Array.isArray(previousBuyGet) ? previousBuyGet : []).forEach((line) => {
    const existingLineId = resolveLineIdFromLineFields(line);
    if (!existingLineId) {
      return;
    }

    const key = buildBuyGetIdentityKey(line);
    if (!queuesByKey.has(key)) {
      queuesByKey.set(key, []);
    }

    queuesByKey.get(key)?.push(existingLineId);
  });

  return (Array.isArray(nextBuyGet) ? nextBuyGet : []).map((line) => {
    const explicitLineId = resolveLineIdFromLineFields(line);
    const key = buildBuyGetIdentityKey(line);
    const keyQueue = queuesByKey.get(key) ?? [];
    const reusedLineId = keyQueue.length > 0 ? keyQueue.shift() ?? null : null;
    const lineId = explicitLineId ?? reusedLineId ?? generateObjectIdLikeLineId();

    return {
      ...line,
      lineId,
      orderProductLineID: getNonEmptyString(line?.orderProductLineID) ?? lineId
    };
  });
};

export const findLineIndexByLineId = (lines: any[] = [], targetLineId: string): number => {
  const normalizedTarget = getNonEmptyString(targetLineId);
  if (!normalizedTarget) {
    return -1;
  }

  return (Array.isArray(lines) ? lines : []).findIndex((line) => {
    return resolveLineId(line) === normalizedTarget;
  });
};

export const normalizeProductIdentity = (line: any): string => {
  return resolveProductId(line);
};

export const normalizeSelectedOptionIdentity = (line: any): string | null => {
  const value = resolveSelectedOptionId(line);
  return value || null;
};

export const isCatalogMergeableLine = (line: any): boolean => {
  return !getNonEmptyString(line?.returnSourceOrderLineId);
};

export const findMergeableCatalogLineIndex = (
  lines: any[] = [],
  productId: string,
  selectedOptionId: string | null
): number => {
  const normalizedProductId = getNonEmptyString(productId) ?? '';
  const normalizedOptionId = getNonEmptyString(selectedOptionId) ?? '';

  return (Array.isArray(lines) ? lines : []).findIndex((line) => {
    return (
      isCatalogMergeableLine(line) &&
      resolveProductId(line) === normalizedProductId &&
      resolveSelectedOptionId(line) === normalizedOptionId
    );
  });
};

export const isMeasuredLineType = (line: any): boolean => {
  return line?.type === ProductType.WEIGHT || line?.type === ProductType.LENGTH;
};

export const normalizeAttributesForLineType = (line: any, nextQuantity?: number): any[] => {
  const currentAttributes = Array.isArray(line?.attributes) ? line.attributes : [];

  if (isMeasuredLineType(line)) {
    return [currentAttributes[0] ?? {}];
  }

  if (nextQuantity === undefined || nextQuantity === null || !Number.isFinite(Number(nextQuantity))) {
    return currentAttributes;
  }

  const safeQuantity = Math.max(0, Math.floor(Number(nextQuantity)));
  return currentAttributes.slice(0, safeQuantity);
};
